import {
    INDOOR_SECURITY_CAMERAS,
    OTHER,
    OUTDOOR_SECURITY_CAMERAS,
} from "./products";
import translations from "./translations.json";
import languages from "./languages.json";
import productTitle from "./utils/productTitle";
import { isOutOfStock } from "./contexts/Cart";
import { DEFAULT_LANGUAGE, LANGUAGES, languageFromPath, pathInLanguage } from "./contexts/Language";

// Title, description, keywords and link-preview tags for every page.
// usePageMeta applies them in the browser, so the tab title follows
// navigation; scripts/prerender.js writes them into a separate HTML file
// per page at build time, because Facebook, WhatsApp, Telegram and other
// link-preview bots don't run JavaScript and would otherwise only ever see
// the home page's tags in index.html.

export const SITE_URL = "https://eufy.ge";
const LOGO = "/images/logo.png";

const CATEGORIES = [
    ["indoor", INDOOR_SECURITY_CAMERAS],
    ["outdoor", OUTDOOR_SECURITY_CAMERAS],
    ["other", OTHER],
];
const PRODUCTS = CATEGORIES.flatMap(([, products]) => products);

const POLICIES = {
    "policies/warranty": "warranty",
    "policies/delivery": "delivery",
    "policies/returns": "returns",
    "policies/privacy": "privacy",
    "policies/terms-and-conditions": "terms",
};
const NO_INDEX = "noindex, nofollow";

// The pages that get their own HTML file at build time, in every language,
// as paths in the default language. Keep in step with the routes in App.jsx.
export const PAGE_PATHS = [
    "",
    "cart",
    "order",
    ...Object.keys(POLICIES),
    ...PRODUCTS.map((product) => product.id),
];

// The 1200x630 JPEG that link previews show for a product; generate it with
// `pnpm share-images`. Previews can't rely on the WebP product photo:
// Facebook only documents JPEG, PNG and GIF.
export function shareImagePath(product) {
    return `/images/share/${product.id}.jpg`;
}

// `pathname` is the browser path, such as "/en/eufyCam-S4". Returns the
// page's language and title, and the tags that go in <head>, each as
// { tag, attrs } plus `text` for a script's contents.
export function pageHead(pathname) {
    const lang = languageFromPath(pathname);
    const path = pathInLanguage(pathname, DEFAULT_LANGUAGE).slice(1);
    const page = pageMeta(path, lang);

    const url = pageUrl(path, lang);
    const image = page.image ?? { url: LOGO, width: 1500, height: 1500 };
    const tags = [
        meta("name", "description", page.description),
        meta("name", "keywords", page.keywords),
        meta("name", "robots", page.robots ?? "index, follow"),
        meta("property", "og:site_name", "eufy.ge"),
        meta("property", "og:type", page.product ? "product" : "website"),
        meta("property", "og:locale", languages.languages[lang].locale),
        meta("property", "og:url", url),
        meta("property", "og:title", page.shareTitle ?? page.title),
        meta("property", "og:description", page.description),
        meta("property", "og:image", SITE_URL + image.url),
        meta("property", "og:image:width", image.width),
        meta("property", "og:image:height", image.height),
        meta("property", "og:image:alt", page.shareTitle ?? page.title),
        meta("name", "twitter:card", page.image ? "summary_large_image" : "summary"),
    ];

    // Pages kept out of search results don't point search engines elsewhere.
    if (!page.robots) {
        tags.push(
            link({ rel: "canonical", href: url }),
            link({ rel: "alternate", hreflang: "x-default", href: pageUrl(path, DEFAULT_LANGUAGE) }),
            ...LANGUAGES.map((language) => link({ rel: "alternate", hreflang: language, href: pageUrl(path, language) }))
        );
    }

    if (page.product) {
        const { product } = page;
        const availability = isOutOfStock(product) ? "OutOfStock" : "InStock";
        tags.push(
            meta("property", "product:price:amount", product.price),
            meta("property", "product:price:currency", "GEL"),
            meta("property", "product:availability", isOutOfStock(product) ? "out of stock" : "in stock"),
            {
                tag: "script",
                attrs: { type: "application/ld+json", "data-page": "" },
                text: JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Product",
                    name: page.shareTitle,
                    sku: product.id,
                    image: SITE_URL + product.imageUrl,
                    description: page.description,
                    offers: {
                        "@type": "Offer",
                        url,
                        price: product.price,
                        priceCurrency: "GEL",
                        availability: `https://schema.org/${availability}`,
                        itemCondition: "https://schema.org/NewCondition",
                        seller: { "@id": SITE_URL },
                    },
                }),
            }
        );
    }

    return { lang, title: page.title, tags };
}

// What sets one page apart: its title (with the site name), description and
// keywords, plus a product's own title, image and offer.
function pageMeta(path, lang) {
    const t = translations[lang].translation;
    const { seo } = t;
    const withSiteName = (title) => `${title} • ${seo["site-name"]}`;
    const pageKeywords = (title) => `${title}, ${seo["brand-keywords"]}`;

    if (path === "") {
        return { title: seo["home-title"], description: seo.description, keywords: seo.keywords };
    }

    if (path === "cart" || path === "order") {
        const title = path === "cart" ? t.cart.title : seo["order-title"];
        return {
            title: withSiteName(title),
            description: seo.description,
            keywords: pageKeywords(title),
            robots: NO_INDEX,
        };
    }

    if (POLICIES[path]) {
        const policy = t[POLICIES[path]];
        return {
            title: withSiteName(policy.title),
            shareTitle: policy.title,
            description: policyDescription(policy, t) || seo.description,
            keywords: pageKeywords(policy.title),
            robots: NO_INDEX,
        };
    }

    const product = PRODUCTS.find(({ id }) => id === path);
    if (product) {
        const title = productTitle(product, lang);
        const [category] = CATEGORIES.find(([, products]) => products.includes(product));
        const features = product.description
            .map(({ key, value }) => `${key[lang]} ${value[lang]}`)
            .join(" · ");
        return {
            title: withSiteName(title),
            shareTitle: title,
            description: fill(seo["product-description"], { title, features, price: product.price }),
            keywords: fill(seo["product-keywords"], { title, category: seo["category-keywords"][category] }),
            image: { url: shareImagePath(product), width: 1200, height: 630 },
            product,
        };
    }

    return {
        title: withSiteName(t["404"]),
        description: seo.description,
        keywords: seo["brand-keywords"],
        robots: NO_INDEX,
    };
}

function pageUrl(path, lang) {
    return SITE_URL + pathInLanguage(`/${path}`, lang);
}

// A policy's summary (or intro) as one line of plain text: "$t(key)"
// references are looked up and link tags such as <privacy>…</privacy> keep
// only their label. Cut at a word near 200 characters, which previews show.
function policyDescription(policy, t) {
    const text = (policy.summary ?? policy.intro ?? [])
        .map((item) =>
            item
                .replace(/\$t\(([\w.-]+)\)/g, (_, key) => key.split(".").reduce((value, part) => value?.[part], t) ?? "")
                .replace(/<\/?\w+>/g, "")
                .trim()
        )
        .filter(Boolean)
        .map((item) => (/[.!?]$/.test(item) ? item : `${item}.`))
        .join(" ");
    return text.length > 200 ? `${text.slice(0, text.lastIndexOf(" ", 200))}…` : text;
}

function fill(template, values) {
    return template.replace(/\{\{(\w+)\}\}/g, (_, name) => values[name]);
}

function meta(attribute, name, content) {
    return { tag: "meta", attrs: { [attribute]: name, content: String(content) } };
}

function link(attrs) {
    return { tag: "link", attrs };
}
